<template>
  <div class="d-flex justify-content-between align-items-center flex-wrap w-100 px-3 mb-3">
    <h2 class="mb-2 mb-md-0">Applications</h2>
    <div class="d-flex flex-wrap gap-2 my-2">
      <input
        v-model="filterName"
        type="text"
        class="form-control"
        placeholder="Filter by Name"
        style="max-width: 200px;"
      />
      <input
        v-model="filterClientId"
        type="text"
        class="form-control"
        placeholder="Filter by Client ID"
        style="max-width: 200px;"
      />
    </div>
    <button
      v-show="isDomainAdmin"
      type="button"
      @click="addApplication"
      class="btn btn-sm btn-success mt-2 mt-md-0"
    >
      <BIconPlusSquare /> Add
    </button>
  </div>
  <table class="table bg-light">
    <thead>
      <tr>
        <th>Name</th>
        <th>Client ID</th>
        <th></th>
      </tr>
    </thead>
    <tbody>
    <template v-for="application in applicationsList" :key="application.clientId">
      <tr :class="{ 'white-background': application.clientId === selectedApplication.clientId }">
        <td>
          <span @click="goToDetails(application.id)" class="text-xl hover-underline" style="cursor: pointer">{{ application.name }}</span>
        </td>
        <td>
          <span>{{ application.clientId }}</span>
        </td>
          <td :class="(isGlobalAdmin || isDomainAdmin) ? 'two-buttons' : 'one-button'" v-if="isGlobalAdmin || isDomainAdmin">
        <button @click="openDeleteModal(application)" type="button" class="btn btn-sm btn-danger ml-2">
          <BIconTrashFill /> Remove
        </button>
      </td>
      </tr>
    </template>
    </tbody>
  </table>
  <div class="d-flex flex-direction-column justify-content-begin align-items-center" style="justify-content: center">
    <button v-show="nextIdentifier !== pageSize.valueOf()" @click="backFive" :disabled="nextIdentifier.valueOf() === pageSize.valueOf()" class="page-link"><BIconSkipBackwardFill/></button>
    <button v-show="nextIdentifier !== pageSize.valueOf()" @click="previousPage" :disabled="nextIdentifier.valueOf() === pageSize.valueOf()" class="page-link"><BIconPlayFill style="transform: scaleX(-1);"/></button>
    <span v-show="pageNumber.valueOf() > 1" @click="toBeginning" class="page-link" style="cursor: pointer;"> 1 </span>
    <span class="page-link border-primary" style="margin-right: 10px; margin-left: 10px; border-width: medium"> {{pageNumber.valueOf()}}</span>
    <span v-show="pageNumber.valueOf() < pagesAmount.valueOf()" @click="toEnd" class="page-link" style="cursor: pointer;">{{pagesAmount.valueOf()}}</span>
    <button v-show="nextIdentifier < applicationsCount" @click="nextPage" :disabled="nextIdentifier.valueOf() >= applicationsCount.valueOf()" class="page-link"><BIconPlayFill/></button>
    <button v-show="nextIdentifier < applicationsCount" @click="forwardFive" :disabled="nextIdentifier.valueOf() >= applicationsCount.valueOf()" class="page-link"><BIconSkipForwardFill/></button>
  </div>
  <!-- Add Application to Database -->
  <AModal
    v-model="addApplicationModalOpen"
    @click-outside="closeModal"
    :style="{ 'max-width': '50rem' }"
    >
      <template #header>
        <div class="d-flex flex-direction-column justify-content-begin align-items-center">
          <h3>Add Application</h3>
        </div>
      </template>
      <template #body>
        <div class="form-group">
          <label>Client ID</label>
          <input
            v-model="newApplicationClientId"
            name="newApplicationClientId"
            id="newApplicationClientId"
            type="text"
            placeholder="Application Client ID"
            size="30"
            class="form-control"/>
          <p v-if="applicationClientIdFormattingErrorFound" class="text-danger">
            Formatting Error: Check that a domain has been selected and the format of your permission name is valid.
            {{ applicationClientIdFormatErrorStr }}
          </p>
        </div>
      </template>
      <template #footer>
        <div class="d-flex justify-content-end w-100">
          <button @click="closeModal" class="btn btn-outline-secondary mr-2">Cancel <BIconXCircleFill /></button>
          <button @click="acceptAddModal" class="btn btn-success mr-2" :disabled="!newApplicationClientId">Add <BIconPlusSquare /></button>
        </div>
      </template>
  </AModal>
  <!-- Modal for deleting an application -->
  <AModal
  v-model="deleteApplicationModalOpen"
  @click-outside="closeModal"
  :style="{ 'max-width': '30rem', 'max-height': '70rem' }">
  <template #header>
    <h3>Delete Application</h3>
  </template>
  <template #body>
    <div class="removeApplicationBody">
      <p class="text-xl text-danger">
        Are you sure you want to remove the application
        <strong>{{ applicationToDelete?.name }}</strong>?
        <br><br>
        All associated permission mappings will also be removed.
      </p>
    </div>
  </template>
  <template #footer>
    <div class="d-flex justify-content-end w-100">
      <button @click="closeModal" class="btn btn-outline-secondary mr-2">
        <BIconXCircleFill /> Cancel
      </button>
      <button @click="acceptDeleteModal" class="btn btn-danger mr-2">
        <BIconTrashFill /> Remove
      </button>
    </div>
  </template>
</AModal>

  <AToast />
</template>

<script setup>
// Grab the list of applications from the applications store
import { useApplicationsStore } from '../stores/applicationsStore.js'
import { storeToRefs } from 'pinia'
import { ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import AToast from '../components/AToast.vue'
import { useUserInfoStore } from '../stores/userInfoStore.js'
import { BIconPlayFill, BIconSkipForwardFill, BIconSkipBackwardFill } from 'bootstrap-icons-vue'
import router from '../router/router'
import AModal from '@/components/AModal.vue'
import { UUIDFormatErrorStr, UUIDRegex } from '@/lib/constants'

const applicationsStore = useApplicationsStore()
const { applicationsList, pageSize, nextIdentifier, applicationsCount, pageNumber, pagesAmount } = storeToRefs(applicationsStore)

// The logged-in user's role is userInfoStore.role
const userInfoStore = useUserInfoStore()
const { isDomainAdmin } = storeToRefs(userInfoStore)

// Interact with router
const route = useRoute()

const selectedApplication = ref({})
const addApplicationModalOpen = ref(false)
const deleteApplicationModalOpen = ref(false)
const applicationToDelete = ref(null)
const newApplicationClientId = ref('')
const applicationClientIdFormatErrorStr = ref('')
const applicationClientIdFormattingErrorFound = ref(false)

const filterName = ref('')
const filterClientId = ref('')

const currentName = ref('')
const currentClientId = ref('')

let debounceTimeout = null

watch([filterName, filterClientId], () => {
  clearTimeout(debounceTimeout)
  debounceTimeout = setTimeout(() => {
    currentName.value = filterName.value.trim()
    currentClientId.value = filterClientId.value.trim()

    applicationsStore.getApplications({
      pageSize: pageSize.value,
      nextIdentifier: 0,
      appName: currentName.value || undefined,
      filterClientId: currentClientId.value || undefined
    })

    pageNumber.value = 1
  }, 600)
})

watch(() => route.path, async () => {
  await applicationsStore.getApplications({ pageSize: 10, nextIdentifier: 0 })
  pageNumber.value = 1
}, { immediate: true })

function addApplication () {
  addApplicationModalOpen.value = true
}

async function nextPage () {
  await applicationsStore.getApplications({
    pageSize: pageSize.value,
    nextIdentifier: nextIdentifier.value,
    appName: currentName.value || undefined,
    filterClientId: filterClientId.value
  })
  pageNumber.value++
}

async function previousPage () {
  const previousIdentifier = nextIdentifier.value - (pageSize.value * 2)
  await applicationsStore.getApplications({
    pageSize: pageSize.value,
    nextIdentifier: previousIdentifier,
    appName: currentName.value || undefined,
    filterClientId: filterClientId.value
  })
  pageNumber.value--
}

async function forwardFive () {
  const diff = pageNumber.value + 5 <= pagesAmount.value ? 5 : pagesAmount.value - pageNumber.value
  const next = nextIdentifier.value + (pageSize.value * (diff - 1))
  await applicationsStore.getApplications({
    pageSize: pageSize.value,
    nextIdentifier: next,
    appName: currentName.value || undefined,
    filterClientId: filterClientId.value
  })
  pageNumber.value += diff
}

async function backFive () {
  const diff = pageNumber.value > 5 ? 5 : pageNumber.value - 1
  const previous = nextIdentifier.value - (pageSize.value * (diff + 1))
  await applicationsStore.getApplications({
    pageSize: pageSize.value,
    nextIdentifier: previous,
    appName: currentName.value || undefined,
    filterClientId: filterClientId.value
  })
  pageNumber.value -= diff
}

async function toBeginning () {
  await applicationsStore.getApplications({
    pageSize: pageSize.value,
    nextIdentifier: 0,
    appName: currentName.value || undefined,
    filterClientId: filterClientId.value
  })
  pageNumber.value = 1
}

async function toEnd () {
  const lastIdentifier = pageSize.value * (pagesAmount.value - 1)
  await applicationsStore.getApplications({
    pageSize: pageSize.value,
    nextIdentifier: lastIdentifier,
    appName: currentName.value || undefined,
    filterClientId: filterClientId.value
  })
  pageNumber.value = pagesAmount.value
}

function goToDetails (id) {
  router.push({ name: 'applicationDetails', params: { id: id } })
}

function closeModal () {
  addApplicationModalOpen.value = false
  applicationClientIdFormattingErrorFound.value = false
  newApplicationClientId.value = ''
  deleteApplicationModalOpen.value = false
  applicationToDelete.value = null
}

function openDeleteModal (application) {
  applicationToDelete.value = application
  deleteApplicationModalOpen.value = true
}

async function acceptAddModal () {
  if (process.env.VUE_APP_ORG === 'BYU' && !UUIDRegex.test(newApplicationClientId.value)) {
    applicationClientIdFormattingErrorFound.value = true
    applicationClientIdFormatErrorStr.value = UUIDFormatErrorStr
    return
  }

  const result = await applicationsStore.createApplication({ clientId: newApplicationClientId.value })
  if (!result) {
    closeModal()
    return
  }

  const newApp = await applicationsStore.getApplicationByClientId(newApplicationClientId.value)

  if (newApp) {
    goToDetails(newApp.id)
  } else {
    console.error('Could not find newly created application.')
  }
  closeModal()
}

async function acceptDeleteModal () {
  if (applicationToDelete.value) {
    await removeApplication(applicationToDelete.value.id)
    closeModal()
  }
}

async function removeApplication (appId) {
  const result = await applicationsStore.deleteApplication(appId)
  if (result) {
    // Re-fetch the current page to reflect changes
    await applicationsStore.getApplications({
      pageSize: pageSize.value,
      nextIdentifier: (pageNumber.value - 1) * pageSize.value,
      filterClientId: filterClientId.value
    })
  }
  return result
}

</script>

<style scoped>
.text-xl {
  font-size: 1.3rem;
  font-weight: 600;
}
.expander {
  transition: transform .3s ease;
}
/* Remove the top border on an expanded row item */
.hide-top-border {
  border-top: none !important;
}
.white-background {
  background-color: white;
}

.hover-underline:hover {
  text-decoration: underline;
}
</style>
